import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Switch,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Badge,
  Fade,
  Grow,
  useTheme,
  useMediaQuery,
  Tooltip,
  Menu,
  MenuItem,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Backdrop,
} from '@mui/material';
import {
  AccountCircle as AccountIcon,
  Storage as StorageIcon,
  Language as LanguageIcon,
  Palette as PaletteIcon,
  Save as SaveIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Analytics as AnalyticsIcon,
  OpenInNew as OpenInNewIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../store';
import { fetchProfile } from '../store/slices/authSlice';
import usePageTitle from '../hooks/usePageTitle';
import { 
  fetchUserStats, 
  fetchRecentActivity, 
  fetchUserSettings, 
  updateUserSettings, 
  uploadAvatar,
  deleteAvatar
} from '../store/slices/profileSlice';
import { useThemeContext } from '../components/theme/ThemeProvider';
import ProfileStats from '../components/profile/ProfileStats';
import RecentActivity from '../components/profile/RecentActivity';
import { useSnackbar } from 'notistack';
import config from '../config';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          <Grow in={value === index} timeout={300}>
            <div>{children}</div>
          </Grow>
        </Box>
      )}
    </div>
  );
}

const Profile: React.FC = () => {
  usePageTitle('Profile');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch<AppDispatch>();
  const { enqueueSnackbar } = useSnackbar();
  const { setMode } = useThemeContext();
  
  // Auth state
  const { user, loading: authLoading, error: authError } = useSelector((state: RootState) => state.auth);
  
  // Profile state
  const { 
    stats, 
    activities, 
    settings, 
    loading: profileLoading, 
    error: profileError, 
  } = useSelector((state: RootState) => state.profile);
  
  // State management
  const [tabValue, setTabValue] = useState(0);
  const [avatarMenuAnchor, setAvatarMenuAnchor] = useState<null | HTMLElement>(null);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState<null | HTMLElement>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const autoRefresh = true;
  
  // Form states (read-only display)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    bio: '',
    location: '',
    website: '',
  });
  
  const [localSettings, setLocalSettings] = useState(() => {
    // Initialize darkMode from localStorage if available
    const savedDarkMode = localStorage.getItem('darkMode');
    return {
      emailNotifications: true,
      pushNotifications: false,
      twoFactorAuth: false,
      darkMode: savedDarkMode ? savedDarkMode === 'true' : theme.palette.mode === 'dark',
      language: 'en',
      autoSave: true,
    };
  });

  // Compute full name from firstName and lastName
  const fullName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
  const initials = fullName.split(' ').map(n => n.charAt(0)).join('').toUpperCase();

  // Load initial data
  useEffect(() => {
    dispatch(fetchProfile());
    dispatch(fetchUserStats());
    dispatch(fetchRecentActivity(10));
    dispatch(fetchUserSettings());
  }, [dispatch]);

  // Auto-refresh data every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      if(document.visibilityState!=='visible')return;
      dispatch(fetchUserStats());
      dispatch(fetchRecentActivity(10));
    }, 30000);
    
    return () => clearInterval(interval);
  }, [dispatch, autoRefresh]);

  // Update form data when user data changes
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.metadata?.phone || '',
        organization: user.metadata?.organization || '',
        bio: user.metadata?.bio || '',
        location: user.metadata?.location || '',
        website: user.metadata?.website || '',
      });
    }
  }, [user]);

  // Update local settings when Redux settings change
  useEffect(() => {
    if (settings) {
      setLocalSettings(settings);
    }
  }, [settings]);

  // Handle settings changes
  const handleSettingsChange = (setting: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setLocalSettings(prev => ({
      ...prev,
      [setting]: newValue
    }));
    
    // Update settings in Redux
    dispatch(updateUserSettings({ [setting]: newValue }));
    
    // If dark mode setting changed, update theme and save to localStorage
    if (setting === 'darkMode') {
      setMode(newValue ? 'dark' : 'light');
      localStorage.setItem('darkMode', String(newValue));
      enqueueSnackbar(`${newValue ? 'Dark' : 'Light'} mode enabled`, { variant: 'success' });
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploadingAvatar(true);
    try {
      await dispatch(uploadAvatar(file));
      setAvatarMenuAnchor(null);
      // Refresh profile data
      dispatch(fetchProfile());
    } catch (error) {
      // Error is handled by the Redux slice
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleAvatarDelete = async () => {
    try {
      await dispatch(deleteAvatar());
      setAvatarMenuAnchor(null);
      // Refresh profile data
      dispatch(fetchProfile());
    } catch (error) {
      // Error is handled by the Redux slice
    }
  };

  const handleManualRefresh = () => {
    dispatch(fetchProfile());
    dispatch(fetchUserStats());
    dispatch(fetchRecentActivity(10));
    dispatch(fetchUserSettings());
    enqueueSnackbar('Data refreshed!', { variant: 'success' });
  };

  const getAccountAge = () => {
    if (!user?.createdAt) return 'Unknown';
    const created = new Date(user.createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} days`;
  };

  const isLoading = authLoading || profileLoading.stats || profileLoading.activities || profileLoading.settings;
  const hasError = authError || profileError;

  if (isLoading && !user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (hasError && !user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {hasError}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {/* Header Section */}
      <Fade in timeout={500}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            mb: 4, 
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              right: 0, 
              width: 200, 
              height: 200, 
              background: `radial-gradient(circle, ${theme.palette.primary.main}10 0%, transparent 70%)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)'
            }} 
          />
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={3} md={2}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    <Tooltip title="Change Avatar">
                      <IconButton
                        size="small"
                        sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' }
                        }}
                        onClick={(e) => setAvatarMenuAnchor(e.currentTarget)}
                      >
                        <PhotoCameraIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <Avatar 
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      bgcolor: 'primary.main',
                      fontSize: 48,
                      fontWeight: 'bold',
                      border: `4px solid ${theme.palette.background.paper}`,
                      boxShadow: theme.shadows[8]
                    }}
                  >
                    {user?.avatar ? (
                      <img src={user.avatar} alt={fullName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      initials || <AccountIcon fontSize="large" />
                    )}
                  </Avatar>
                </Badge>
                
                {isUploadingAvatar && (
                  <Backdrop
                    sx={{ 
                      color: '#fff', 
                      zIndex: (theme) => theme.zIndex.drawer + 1,
                      borderRadius: '50%',
                      width: 120,
                      height: 120
                    }}
                    open={isUploadingAvatar}
                  >
                    <CircularProgress color="inherit" />
                  </Backdrop>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={9} md={7}>
              <Box>
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {fullName || 'User Profile'}
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {user?.email || 'user@example.com'}
                </Typography>
                {user?.metadata?.bio && (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {user.metadata.bio}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {user?.role && (
                    <Chip 
                      label={user.role} 
                      color="primary" 
                      variant="outlined" 
                      size="small" 
                    />
                  )}
                  {user?.metadata?.organization && (
                    <Chip 
                      label={user.metadata.organization} 
                      color="secondary" 
                      variant="outlined" 
                      size="small" 
                    />
                  )}
                  <Chip 
                    label={`Member for ${getAccountAge()}`} 
                    color="default" 
                    variant="outlined" 
                    size="small" 
                    icon={<CalendarIcon />}
                  />
                  {stats && (
                    <Chip 
                      label={`${stats.totalFiles} files`} 
                      color="info" 
                      variant="outlined" 
                      size="small" 
                      icon={<StorageIcon />}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<InfoIcon />}
                  endIcon={<OpenInNewIcon />}
                  href={config.server.oneOrigin}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                  sx={{ borderRadius: 2 }}
                >
                  Profile managed by SSO – CYNAYD One
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SettingsIcon />}
                  onClick={(e) => setSettingsMenuAnchor(e.currentTarget)}
                  fullWidth
                  sx={{ borderRadius: 2 }}
                >
                  Settings
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleManualRefresh}
                  disabled={isLoading}
                  fullWidth
                  sx={{ borderRadius: 2 }}
                >
                  Refresh
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Fade>

      {/* Quick Stats Section */}
      {stats && (
        <Fade in timeout={600}>
          <Box sx={{ mb: 4 }}>
            <ProfileStats stats={stats} />
          </Box>
        </Fade>
      )}

      {/* Recent Activity Section */}
      {activities.length > 0 && (
        <Fade in timeout={700}>
          <Box sx={{ mb: 4 }}>
            <RecentActivity activities={activities} maxItems={5} />
          </Box>
        </Fade>
      )}

      {/* Tabs Section */}
      <Paper elevation={2} sx={{ borderRadius: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="profile tabs"
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons={isMobile ? "auto" : false}
            sx={{
              '& .MuiTab-root': {
                minHeight: 64,
                fontSize: '1rem',
                fontWeight: 500,
              }
            }}
          >
            <Tab icon={<AccountIcon />} label="Account" />
            <Tab icon={<PaletteIcon />} label="Preferences" />
            <Tab icon={<AnalyticsIcon />} label="Analytics" />
          </Tabs>
        </Box>

        {/* Account Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
              Profile details are managed by SSO – CYNAYD One and cannot be edited here.
            </Alert>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  disabled
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  disabled
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  disabled
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  disabled
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Organization"
                  name="organization"
                  value={formData.organization}
                  disabled
                  helperText="Organization is auto-filled from your tenant"
                  InputProps={{
                    startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={formData.location}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Website"
                  name="website"
                  value={formData.website}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bio"
                  name="bio"
                  value={formData.bio}
                  disabled
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Preferences Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <PaletteIcon sx={{ mr: 1 }} />
                  Appearance
                </Typography>
                
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <PaletteIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Dark Mode"
                      secondary="Use dark theme"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={localSettings.darkMode}
                        onChange={handleSettingsChange('darkMode')}
                        color="primary"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
            
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <LanguageIcon sx={{ mr: 1 }} />
                  General Settings
                </Typography>
                
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <SaveIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Auto Save"
                      secondary="Automatically save changes"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={localSettings.autoSave}
                        onChange={handleSettingsChange('autoSave')}
                        color="primary"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Box>
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {stats && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <ProfileStats stats={stats} />
                </Grid>
                <Grid item xs={12}>
                  <RecentActivity activities={activities} maxItems={10} />
                </Grid>
              </Grid>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* Avatar Menu */}
      <Menu
        anchorEl={avatarMenuAnchor}
        open={Boolean(avatarMenuAnchor)}
        onClose={() => setAvatarMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => document.getElementById('avatar-upload')?.click()}>
          <PhotoCameraIcon sx={{ mr: 1 }} />
          Upload Photo
        </MenuItem>
        <MenuItem onClick={handleAvatarDelete}>
          <DeleteIcon sx={{ mr: 1 }} />
          Remove Photo
        </MenuItem>
      </Menu>

      {/* Settings Menu */}
      <Menu
        anchorEl={settingsMenuAnchor}
        open={Boolean(settingsMenuAnchor)}
        onClose={() => setSettingsMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => { setTabValue(1); setSettingsMenuAnchor(null); }}>
          <PaletteIcon sx={{ mr: 1 }} />
          Preferences
        </MenuItem>
        <MenuItem onClick={() => { setTabValue(2); setSettingsMenuAnchor(null); }}>
          <AnalyticsIcon sx={{ mr: 1 }} />
          Analytics
        </MenuItem>
      </Menu>

      {/* Hidden file input for avatar upload */}
      <input
        id="avatar-upload"
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleAvatarUpload}
      />

      {/* Floating Action Button for Quick Actions */}
      <SpeedDial
        ariaLabel="Quick actions"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        icon={<SpeedDialIcon />}
      >
        <SpeedDialAction
          icon={<RefreshIcon />}
          tooltipTitle="Refresh"
          onClick={handleManualRefresh}
        />
        <SpeedDialAction
          icon={<AnalyticsIcon />}
          tooltipTitle="Analytics"
          onClick={() => setTabValue(2)}
        />
      </SpeedDial>
    </Container>
  );
};

export default Profile;
